// frontend/src/components/chat/ChatInput.jsx
// UPDATED: Added file attachment support
// Chat input with message and file sending

import React, { useState, useRef, useCallback } from "react";
import FileUploadButton from "./FileUploadButton";
import AttachmentPreview from "./AttachmentPreview";
import { uploadAttachment } from "../../utils/chatAttachmentApi";

const ChatInput = ({ onSend, onTyping, disabled = false }) => {
  const [message, setMessage] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const textareaRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  // Handle text input
  const handleChange = (e) => {
    setMessage(e.target.value);

    // Auto-resize textarea
    const textarea = e.target;
    textarea.style.height = "auto";
    textarea.style.height = Math.min(textarea.scrollHeight, 120) + "px";

    // Handle typing indicator
    if (onTyping) {
      onTyping(true);

      // Clear existing timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }

      // Stop typing after 2 seconds of no input
      typingTimeoutRef.current = setTimeout(() => {
        onTyping(false);
      }, 2000);
    }
  };

  // Handle file selection
  const handleFileSelect = (file) => {
    setSelectedFile(file);
  };

  // Remove selected file
  const handleRemoveFile = () => {
    setSelectedFile(null);
    setUploadProgress(0);
  };

  // Handle send
  const handleSend = useCallback(async () => {
    const trimmedMessage = message.trim();

    // Must have message or file
    if (!trimmedMessage && !selectedFile) return;

    // Stop typing indicator
    if (onTyping) {
      onTyping(false);
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    }

    try {
      let attachmentData = null;

      // Upload file first if selected
      if (selectedFile) {
        setIsUploading(true);
        setUploadProgress(0);

        try {
          const result = await uploadAttachment(
            selectedFile,
            null, // conversationId will be set by parent
            (progress) => setUploadProgress(progress)
          );
          attachmentData = result.attachment;
        } catch (err) {
          console.error("Failed to upload file:", err);
          alert("Failed to upload file. Please try again.");
          setIsUploading(false);
          return;
        }

        setIsUploading(false);
        setSelectedFile(null);
        setUploadProgress(0);
      }

      // Send message with attachment info
      await onSend(trimmedMessage || null, attachmentData);

      // Clear message
      setMessage("");

      // Reset textarea height
      if (textareaRef.current) {
        textareaRef.current.style.height = "auto";
      }
    } catch (err) {
      console.error("Failed to send:", err);
    }
  }, [message, selectedFile, onSend, onTyping]);

  // Handle key press
  const handleKeyDown = (e) => {
    // Send on Enter (without Shift)
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const canSend = (message.trim() || selectedFile) && !isUploading && !disabled;

  return (
    <div className="chat-input-container">
      {/* Attachment Preview */}
      {selectedFile && (
        <AttachmentPreview
          file={selectedFile}
          uploadProgress={uploadProgress}
          onRemove={handleRemoveFile}
          isUploading={isUploading}
        />
      )}

      {/* Input Area */}
      <div className="chat-input-wrapper">
        <FileUploadButton
          onFileSelect={handleFileSelect}
          disabled={disabled || isUploading || !!selectedFile}
        />

        <textarea
          ref={textareaRef}
          className="chat-input"
          placeholder="Type a message..."
          value={message}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          disabled={disabled || isUploading}
          rows={1}
        />

        <button
          type="button"
          className="chat-send-btn"
          onClick={handleSend}
          disabled={!canSend}
          aria-label="Send message"
        >
          {isUploading ? (
            <span className="spinner-border spinner-border-sm" />
          ) : (
            <i className="bi bi-send-fill"></i>
          )}
        </button>
      </div>

      <div className="chat-input-hint">
        Press Enter to send, Shift+Enter for new line
      </div>
    </div>
  );
};

export default ChatInput;
